import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { bulkCreateUsersAPI, getAdminsForServerAPI } from '../services/serverService';

const JOB_TITLES_FALLBACK = ['Care Worker', 'Senior Carer', 'Nurse', 'Manager', 'Coordinator'];

const BulkUploadModal = ({ serverId, role, closeModal, refreshList }) => {
    const [parsedData, setParsedData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState(null);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [failedEntriesModal, setFailedEntriesModal] = useState(null);

    const [admins, setAdmins] = useState([]);
    const [batchSettings, setBatchSettings] = useState({
        jobTitle: 'Care Worker',
        assignedAdminId: '',
        dob: '1900-01-01',
        addressText: 'TBD'
    });

    // Fetch Admins for the Dropdown if we are uploading Employees
    useEffect(() => {
        if (role === 'employee') {
            const fetchAdmins = async () => {
                try {
                    const response = await getAdminsForServerAPI(serverId);
                    setAdmins(response.data?.data || []);
                } catch (error) {
                    console.error("Failed to fetch admins for batch settings", error);
                }
            };
            fetchAdmins();
        }
    }, [role, serverId]);

    const handleDownloadTemplate = () => {
        const csvContent = "Name,Email,Phone Number\n";
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${role}_upload_template.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // --- PRE-VALIDATION LOGIC ---
    const validateRow = (row) => {
        if (!row.fullName || row.fullName.trim() === '') return 'Name is required.';
        if (role !== 'serviceUser' && (!row.email || row.email.trim() === '')) return 'Email is required.';
        const phoneDigits = row.phoneNumber ? row.phoneNumber.replace(/\D/g, '') : '';
        if (phoneDigits.length < 10 && phoneDigits.length > 0) return 'Phone number too short.';
        if (phoneDigits.length === 0) return 'Phone number is required.';
        return null;
    };

    // --- 2. CSV PARSER & SMART AUTO-GENERATOR ---
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const rows = results.data;
                const processedRows = rows.map(row => {
                    const fullName = (row['Name'] || row.fullName || row.full_name || '').trim();
                    const phone = (row['Phone Number'] || row.phoneNumber || row.phone || '').trim();
                    let email = (row['Email'] || row.email || '').trim();
                    
                    if (role === 'serviceUser' && !email && fullName) {
                        const cleanName = fullName.replace(/[^a-zA-Z]/g, '').toLowerCase();
                        email = `${cleanName}@healthautomatedserviceuser.co.uk`;
                    }

                    const randomNum = Math.floor(10000 + Math.random() * 90000);
                    const passcode = `Architecture${randomNum}!`;

                    const baseUsername = fullName.replace(/\s+/g, '') || 'User';
                    const phoneTail = phone.replace(/\D/g, '').slice(-3) || '000';
                    const suggestedUsername = `${baseUsername}${phoneTail}`;

                    const newRow = { fullName, phoneNumber: phone, email, username: suggestedUsername, passcode };
                    newRow._error = validateRow(newRow);
                    return newRow;
                }).filter(r => r.fullName !== '');

                setParsedData(processedRows);
                // Clear the file input so the user can re-upload the same file if needed
                e.target.value = null;
            }
        });
    };

    // --- Handle Cell Edit ---
    const handleCellEdit = (index, field, value) => {
        const updated = [...parsedData];
        updated[index][field] = value;
        updated[index]._error = validateRow(updated[index]);
        updated[index]._serverError = null; // Clear server error on edit to reset UI
        setParsedData(updated);
    };

    const handleImportClick = () => {
        if (parsedData.length === 0) return alert("No valid data to import.");
        
        const invalidRowsCount = parsedData.filter(r => r._error).length;
        
        if (invalidRowsCount > 0) {
            setShowConfirmModal(true);
        } else {
            executeBulkCreate(parsedData);
        }
    };

    const executeBulkCreate = async (dataToProcess) => {
        setShowConfirmModal(false);
        setIsLoading(true);

        const payloadArray = dataToProcess.map(row => {
            const { _error, _serverError, ...rowPayload } = row;
            if (role === 'employee') {
                rowPayload.jobTitle = batchSettings.jobTitle;
                rowPayload.assignedAdminId = batchSettings.assignedAdminId;
            }
            if (role === 'serviceUser') {
                rowPayload.dob = batchSettings.dob;
                rowPayload.firstLineOfAddress = batchSettings.addressText;
            }
            return rowPayload;
        });

        try {
            const response = await bulkCreateUsersAPI({ targetServerId: serverId, usersArray: payloadArray, role: role });
            const { successCount, createdUsers, errors } = response.data.data;
            
            if (successCount > 0) refreshList(); 

            if (errors && errors.length > 0) {
                // Identify failed rows based on error name
                const failedRows = [];
                dataToProcess.forEach(row => {
                    const err = errors.find(e => e.name === row.fullName);
                    if (err) {
                        failedRows.push({ ...row, _serverError: err.reason });
                    }
                });
                
                const previouslySkippedRows = parsedData.filter(r => r._error);
                
                // Keep the failed/skipped rows in the UI for in-browser correction
                setParsedData([...failedRows, ...previouslySkippedRows]);
                setFailedEntriesModal({ successCount, failCount: failedRows.length, errors, createdUsers });
            } else {
                // Complete success
                setResults({ successCount, createdUsers, errors });
                setParsedData([]);
            }

        } catch (error) {
            console.error("Bulk Upload Error:", error);
            alert("Upload failed: " + (error.message || "Server Error"));
        } finally {
            setIsLoading(false);
        }
    };

    // --- CSV GENERATOR FOR FAILED ENTRIES ---
    const downloadFailedCSV = () => {
        const csvData = parsedData.map(row => ({
            Name: row.fullName,
            Email: row.email,
            'Phone Number': row.phoneNumber,
            'Error Reason': row._serverError || row._error || 'Unknown Error'
        }));
        
        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `failed_${role}_entries_${new Date().getTime()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // --- 4. PDF GENERATOR ---
    const downloadPDF = (createdUsersArray) => {
        if (!createdUsersArray || createdUsersArray.length === 0) return;
        const doc = new jsPDF();
        
        doc.setFontSize(18);
        doc.text(`Created ${role.toUpperCase()} Credentials`, 14, 22);
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

        const tableColumn = ["Name", "Email", "Phone", "Username", "Password"];
        const tableRows = createdUsersArray.map(user => [
            user.fullName || "N/A",
            user.email || "N/A",
            user.phoneNumber || "N/A",
            user.newUsername,
            user.plainTextPassword
        ]);

        autoTable(doc, {
            head: [tableColumn], body: tableRows, startY: 35, theme: 'grid', styles: { fontSize: 9, cellPadding: 3 }, headStyles: { fillColor: [43, 108, 176] }, 
        });

        doc.save(`${role}_credentials_${new Date().getTime()}.pdf`);
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '1000px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px', marginBottom: '20px' }}>
                    <h2 style={{ margin: 0, color: '#2d3748', textTransform: 'capitalize' }}>Bulk Import {role}s</h2>
                    <button onClick={closeModal} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#a0aec0' }}>&times;</button>
                </div>

                {!results ? (
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        
                        <div style={{ backgroundColor: '#ebf8ff', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #bee3f8' }}>
                            <p style={{ margin: '0 0 10px 0', color: '#2b6cb0', fontWeight: 'bold' }}>Step 1: Use the simple 3-column template</p>
                            <button onClick={handleDownloadTemplate} style={{ padding: '8px 16px', backgroundColor: 'white', border: '1px solid #cbd5e0', borderRadius: '4px', cursor: 'pointer', marginRight: '15px', fontWeight: 'bold' }}>
                                ⬇️ Download Template (Name, Email, Phone)
                            </button>
                            <span style={{ fontSize: '14px', color: '#4a5568' }}>Emails are {role === 'serviceUser' ? 'optional' : 'REQUIRED'}.</span>
                        </div>

                        {/* --- BATCH DEFAULTS PANEL --- */}
                        <div style={{ backgroundColor: '#fffaf0', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #feebc8' }}>
                            <p style={{ margin: '0 0 10px 0', color: '#c05621', fontWeight: 'bold' }}>Step 2: Batch Settings (Applied to all rows)</p>
                            {role === 'employee' && (
                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '12px', color: '#718096', display: 'block', marginBottom: '4px' }}>Job Title</label>
                                        <select value={batchSettings.jobTitle} onChange={(e) => setBatchSettings({...batchSettings, jobTitle: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0' }}>
                                            {JOB_TITLES_FALLBACK.map(title => <option key={title} value={title}>{title}</option>)}
                                        </select>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '12px', color: '#718096', display: 'block', marginBottom: '4px' }}>Assigned Admin</label>
                                        <select value={batchSettings.assignedAdminId} onChange={(e) => setBatchSettings({...batchSettings, assignedAdminId: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0' }}>
                                            <option value="">Unassigned</option>
                                            {admins.map(a => <option key={a.id} value={a.username}>{a.fullName} ({a.username})</option>)}
                                        </select>
                                    </div>
                                </div>
                            )}
                            {role === 'serviceUser' && (
                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '12px', color: '#718096', display: 'block', marginBottom: '4px' }}>Default Date of Birth</label>
                                        <input type="date" value={batchSettings.dob} onChange={(e) => setBatchSettings({...batchSettings, dob: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '12px', color: '#718096', display: 'block', marginBottom: '4px' }}>Default Address</label>
                                        <input type="text" value={batchSettings.addressText} onChange={(e) => setBatchSettings({...batchSettings, addressText: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={{ backgroundColor: '#f7fafc', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
                            <p style={{ margin: '0 0 10px 0', color: '#4a5568', fontWeight: 'bold' }}>Step 3: Upload CSV Data</p>
                            <input type="file" accept=".csv" onChange={handleFileUpload} style={{ padding: '10px', width: '100%', cursor: 'pointer' }} />
                        </div>

                        {/* --- Editable Input Fields in Preview Table --- */}
                        {parsedData.length > 0 && (
                            <div>
                                <h3 style={{ fontSize: '16px', color: '#2d3748', marginBottom: '10px' }}>
                                    Preview ({parsedData.length} entries) 
                                    {parsedData.filter(r => r._error || r._serverError).length > 0 && 
                                        <span style={{ color: '#e53e3e', marginLeft: '10px', fontSize: '14px' }}>
                                            ({parsedData.filter(r => r._error || r._serverError).length} errors found)
                                        </span>
                                    }
                                </h3>
                                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', maxHeight: '300px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                        <thead style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
                                            <tr>
                                                <th style={{ padding: '10px', textAlign: 'left', width: '25%' }}>Name</th>
                                                <th style={{ padding: '10px', textAlign: 'left', width: '25%' }}>Email</th>
                                                <th style={{ padding: '10px', textAlign: 'left', width: '25%' }}>Phone</th>
                                                <th style={{ padding: '10px', textAlign: 'left', width: '25%' }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {parsedData.map((row, index) => {
                                                const hasError = !!row._error || !!row._serverError;
                                                const errorMsg = row._serverError || row._error;
                                                return (
                                                    <tr key={index} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: hasError ? '#fff5f5' : 'transparent' }}>
                                                        <td style={{ padding: '8px' }}>
                                                            <input 
                                                                type="text" 
                                                                value={row.fullName} 
                                                                onChange={(e) => handleCellEdit(index, 'fullName', e.target.value)} 
                                                                style={{ width: '100%', padding: '6px', border: `1px solid ${!row.fullName ? '#e53e3e' : '#cbd5e0'}`, borderRadius: '4px' }} 
                                                            />
                                                        </td>
                                                        <td style={{ padding: '8px' }}>
                                                            <input 
                                                                type="text" 
                                                                value={row.email} 
                                                                onChange={(e) => handleCellEdit(index, 'email', e.target.value)} 
                                                                style={{ width: '100%', padding: '6px', border: `1px solid ${(role !== 'serviceUser' && !row.email) ? '#e53e3e' : '#cbd5e0'}`, borderRadius: '4px' }} 
                                                            />
                                                        </td>
                                                        <td style={{ padding: '8px' }}>
                                                            <input 
                                                                type="text" 
                                                                value={row.phoneNumber} 
                                                                onChange={(e) => handleCellEdit(index, 'phoneNumber', e.target.value)} 
                                                                style={{ width: '100%', padding: '6px', border: `1px solid ${(!row.phoneNumber || row.phoneNumber.length < 10) ? '#e53e3e' : '#cbd5e0'}`, borderRadius: '4px' }} 
                                                            />
                                                        </td>
                                                        <td style={{ padding: '8px', color: hasError ? '#e53e3e' : '#38a169', fontWeight: '500', fontSize: '12px' }}>
                                                            {hasError ? `⚠️ ${errorMsg}` : '✅ Ready'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    // --- SUCCESS COMPLETE VIEW ---
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ fontSize: '50px', marginBottom: '10px' }}>✅</div>
                        <h2 style={{ margin: '0 0 10px 0', color: '#2d3748' }}>Import Complete</h2>
                        <p style={{ fontSize: '18px', color: '#4a5568', margin: '0 0 20px 0' }}>Successfully created <strong style={{ color: '#38a169' }}>{results.successCount}</strong> accounts.</p>
                        
                        {results.successCount > 0 && (
                            <button onClick={() => downloadPDF(results.createdUsers)} style={{ padding: '12px 24px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer', boxShadow: '0 4px 6px rgba(49, 130, 206, 0.3)' }}>
                                📄 Download PDF Credentials
                            </button>
                        )}
                    </div>
                )}

                {/* --- BOTTOM ACTION BAR --- */}
                <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button onClick={closeModal} style={{ padding: '10px 20px', backgroundColor: 'white', border: '1px solid #cbd5e0', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', color: '#4a5568' }}>
                        {results ? 'Close' : 'Cancel'}
                    </button>
                    {!results && (
                        <button 
                            onClick={handleImportClick} 
                            disabled={isLoading || parsedData.length === 0} 
                            style={{ 
                                padding: '10px 20px', 
                                backgroundColor: parsedData.length > 0 ? '#38a169' : '#a0aec0', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '6px', 
                                cursor: (isLoading || parsedData.length === 0) ? 'not-allowed' : 'pointer', 
                                fontWeight: 'bold' 
                            }}>
                            {isLoading ? 'Processing...' : (parsedData.length > 0 ? `Import ${parsedData.length} Accounts` : 'Import Accounts')}
                        </button>
                    )}
                </div>

                {/* --- CUSTOM MODALS OVERLAYS --- */}

                {/* 1. Pre-Validation Skip Modal */}
                {showConfirmModal && (
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(255,255,255,0.9)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000, borderRadius: '12px' }}>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', maxWidth: '450px', textAlign: 'center' }}>
                            <div style={{ fontSize: '40px', marginBottom: '10px' }}>⚠️</div>
                            <h3 style={{ margin: '0 0 10px 0', color: '#2d3748' }}>Invalid Entries Detected</h3>
                            <p style={{ color: '#4a5568', marginBottom: '20px' }}>
                                There are <strong>{parsedData.filter(r => r._error).length}</strong> rows with errors. <br/>
                                Would you like to skip the invalid rows and only upload the <strong>{parsedData.filter(r => !r._error).length}</strong> correct ones?
                            </p>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                                <button onClick={() => setShowConfirmModal(false)} style={{ padding: '8px 16px', backgroundColor: 'white', border: '1px solid #cbd5e0', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Cancel & Fix</button>
                                <button onClick={() => executeBulkCreate(parsedData.filter(r => !r._error))} style={{ padding: '8px 16px', backgroundColor: '#e53e3e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                                    Skip Errors & Upload Valid
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 2. Post-Upload Failed Entries Modal */}
                {failedEntriesModal && (
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(255,255,255,0.95)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000, borderRadius: '12px' }}>
                        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px rgba(0,0,0,0.15)', maxWidth: '500px', textAlign: 'center' }}>
                            <div style={{ fontSize: '40px', marginBottom: '10px' }}>⚠️</div>
                            <h3 style={{ margin: '0 0 10px 0', color: '#c53030' }}>Partial Import Success</h3>
                            <p style={{ color: '#4a5568', marginBottom: '10px' }}>
                                Successfully imported <strong style={{ color: '#38a169' }}>{failedEntriesModal.successCount}</strong> accounts.
                            </p>
                            <p style={{ color: '#4a5568', marginBottom: '20px' }}>
                                However, <strong>{failedEntriesModal.failCount}</strong> entries were rejected by the server (e.g. duplicate emails or invalid data). 
                                The failed entries have been kept in the table behind this prompt so you can edit and retry them.
                            </p>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <button onClick={downloadFailedCSV} style={{ padding: '10px 16px', backgroundColor: '#edf2f7', border: '1px solid #cbd5e0', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', color: '#2d3748' }}>
                                    ⬇️ Download CSV of Failed Entries
                                </button>
                                {failedEntriesModal.successCount > 0 && (
                                    <button onClick={() => downloadPDF(failedEntriesModal.createdUsers)} style={{ padding: '10px 16px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
                                        📄 Download PDF Credentials for Successful ({failedEntriesModal.successCount})
                                    </button>
                                )}
                                <button onClick={() => setFailedEntriesModal(null)} style={{ padding: '10px 16px', backgroundColor: '#38a169', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', marginTop: '10px' }}>
                                    Go Back & Fix Failed Entries In-Browser
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default BulkUploadModal;